import * as Y from "yjs";
import type { IndexeddbPersistence } from "./indexeddb-persistence";
import { scheduleStaleIndexedDbCleanup } from "./stale-indexeddb-cleaner";

export const SHARED_YJS_DB_NAME = "illumi-local";
export const SHARED_YJS_DB_VERSION = 2;
export const customStoreName = "custom";
export const updatesStoreName = "updates";
export const updatesByDocIndexName = "by_doc";
export const PREFERRED_TRIM_SIZE = 500;

export interface StoredUpdateRecord {
	doc: string;
	update: Uint8Array;
}

export type StoredDocUpdateEntry = StoredUpdateRecord;

function isStoredUpdateRecord(value: unknown): value is StoredUpdateRecord {
	return (
		typeof value === "object" &&
		value !== null &&
		typeof (value as { doc?: unknown }).doc === "string" &&
		(value as { update?: unknown }).update instanceof Uint8Array
	);
}

export function getScopedCustomKey(
	docName: string,
	key: IDBValidKey,
): IDBValidKey {
	return `${docName}::${String(key)}`;
}

export function rtop<T>(request: IDBRequest<T>): Promise<T> {
	return new Promise<T>((resolve, reject) => {
		request.onerror = () =>
			reject(request.error ?? new Error("IndexedDB request failed"));
		request.onsuccess = () => resolve(request.result);
	});
}

function ensureByDocIndex(store: IDBObjectStore | null | undefined) {
	if (
		store &&
		typeof store.createIndex === "function" &&
		!store.indexNames?.contains(updatesByDocIndexName)
	) {
		store.createIndex(updatesByDocIndexName, "doc", { unique: false });
	}
}

export function createRequiredStores(
	db: IDBDatabase,
	upgradeTx?: IDBTransaction | null,
) {
	if (!db.objectStoreNames.contains(updatesStoreName)) {
		ensureByDocIndex(
			db.createObjectStore(updatesStoreName, { autoIncrement: true }),
		);
	} else if (upgradeTx) {
		ensureByDocIndex(upgradeTx.objectStore(updatesStoreName));
	}
	if (!db.objectStoreNames.contains(customStoreName)) {
		db.createObjectStore(customStoreName);
	}
}

export function hasRequiredStores(db: IDBDatabase): boolean {
	return (
		db.objectStoreNames.contains(updatesStoreName) &&
		db.objectStoreNames.contains(customStoreName)
	);
}

let sharedDb: IDBDatabase | null = null;
let sharedDbOpenPromise: Promise<IDBDatabase> | null = null;
const activePersistenceInstances = new Set<IndexeddbPersistence>();

function closeDbQuietly(db: IDBDatabase | null) {
	if (!db) return;
	try {
		db.close();
	} catch {
		// Ignore errors closing an already-closing connection
	}
}

function notifySharedDbClosed(closedDb: IDBDatabase) {
	if (sharedDb === closedDb) sharedDb = null;
	for (const instance of activePersistenceInstances) {
		if (instance.db === closedDb) instance.db = null;
	}
}

export function registerPersistenceInstance(instance: IndexeddbPersistence) {
	activePersistenceInstances.add(instance);
}

export function unregisterPersistenceInstance(instance: IndexeddbPersistence) {
	activePersistenceInstances.delete(instance);
	if (activePersistenceInstances.size === 0 && sharedDb) {
		const dbToClose = sharedDb;
		sharedDb = null;
		sharedDbOpenPromise = null;
		closeDbQuietly(dbToClose);
	}
}

export function invalidateSharedDB(brokenDb?: IDBDatabase | null) {
	const target = brokenDb ?? sharedDb;
	if (target && sharedDb === target) sharedDb = null;
	sharedDbOpenPromise = null;
	closeDbQuietly(target ?? null);
}

export function openRawDB(
	dbName: string = SHARED_YJS_DB_NAME,
): Promise<IDBDatabase> {
	if (dbName === SHARED_YJS_DB_NAME) {
		if (sharedDb) return Promise.resolve(sharedDb);
		if (sharedDbOpenPromise) return sharedDbOpenPromise;
	}

	const openPromise = new Promise<IDBDatabase>((resolve, reject) => {
		if (typeof indexedDB === "undefined" || !indexedDB?.open) {
			reject(new Error("IndexedDB is not available in this environment"));
			return;
		}
		try {
			const request =
				dbName === SHARED_YJS_DB_NAME
					? indexedDB.open(dbName, SHARED_YJS_DB_VERSION)
					: indexedDB.open(dbName);
			request.onupgradeneeded = () => {
				createRequiredStores(request.result, request.transaction);
			};
			request.onerror = () =>
				reject(
					request.error ?? new Error(`Failed to open IndexedDB ${dbName}`),
				);
			request.onsuccess = () => {
				const db = request.result;
				if (dbName === SHARED_YJS_DB_NAME) {
					if (activePersistenceInstances.size === 0) {
						closeDbQuietly(db);
						sharedDbOpenPromise = null;
						resolve(db);
						return;
					}
					sharedDb = db;
					db.onversionchange = () => {
						notifySharedDbClosed(db);
						closeDbQuietly(db);
					};
					db.onclose = () => notifySharedDbClosed(db);
					scheduleStaleIndexedDbCleanup();
				}
				resolve(db);
			};
		} catch (err) {
			reject(err);
		}
	}).finally(() => {
		if (dbName === SHARED_YJS_DB_NAME) sharedDbOpenPromise = null;
	});

	if (dbName === SHARED_YJS_DB_NAME) sharedDbOpenPromise = openPromise;
	return openPromise;
}

export function resetSharedDatabase(): Promise<void> {
	invalidateSharedDB(sharedDb);
	if (typeof indexedDB === "undefined" || !indexedDB?.deleteDatabase) {
		return Promise.resolve();
	}
	try {
		return rtop(indexedDB.deleteDatabase(SHARED_YJS_DB_NAME)).then(
			() => undefined,
		);
	} catch (err) {
		return Promise.reject(err);
	}
}

export function clearAllLocalDocuments(): Promise<void> {
	for (const instance of Array.from(activePersistenceInstances)) {
		void instance.destroy();
	}
	activePersistenceInstances.clear();
	return resetSharedDatabase().catch(() => undefined);
}

function readDocEntriesWithKeys(
	updatesStore: IDBObjectStore,
	docName: string,
	lowerRef = 0,
): Promise<Array<{ key: number; value: unknown }>> {
	const index = updatesStore.index(updatesByDocIndexName);
	const docRange = IDBKeyRange.only(docName);
	return Promise.all([
		rtop<unknown[]>(index.getAll(docRange)),
		rtop<IDBValidKey[]>(index.getAllKeys(docRange)),
	]).then(([values, keys]) =>
		values
			.map((value, idx) => ({
				key: typeof keys[idx] === "number" ? (keys[idx] as number) : idx + 1,
				value,
			}))
			.filter((e) => e.key >= lowerRef),
	);
}

function readDocKeys(
	updatesStore: IDBObjectStore,
	docName: string,
): Promise<number[]> {
	const index = updatesStore.index(updatesByDocIndexName);
	return rtop<IDBValidKey[]>(index.getAllKeys(IDBKeyRange.only(docName))).then(
		(keys) => keys.filter((k): k is number => typeof k === "number"),
	);
}

export function clearDocument(name: string): Promise<void> {
	if (typeof indexedDB === "undefined" || !indexedDB?.open) {
		return Promise.resolve();
	}
	const runClear = (db: IDBDatabase): Promise<void> => {
		if (!hasRequiredStores(db)) return Promise.resolve();
		try {
			const tx = db.transaction(
				[updatesStoreName, customStoreName],
				"readwrite",
			);
			const updatesStore = tx.objectStore(updatesStoreName);
			const customStore = tx.objectStore(customStoreName);
			const prefix = `${name}::`;
			const clearUpdates = readDocKeys(updatesStore, name).then((keys) =>
				Promise.all(keys.map((key) => rtop(updatesStore.delete(key)))),
			);
			const clearCustom =
				typeof customStore.getAllKeys === "function"
					? rtop<IDBValidKey[]>(customStore.getAllKeys()).then((keys) =>
							Promise.all(
								keys
									.filter((k) => typeof k === "string" && k.startsWith(prefix))
									.map((k) => rtop(customStore.delete(k))),
							),
						)
					: Promise.resolve([]);
			return Promise.all([clearUpdates, clearCustom]).then(() => undefined);
		} catch {
			return Promise.resolve();
		}
	};

	if (sharedDb) return runClear(sharedDb);
	if (sharedDbOpenPromise) return sharedDbOpenPromise.then(runClear);
	return Promise.resolve();
}

export function fetchUpdates(
	idbPersistence: IndexeddbPersistence,
	beforeApplyUpdatesCallback: (updatesStore: IDBObjectStore) => void = () => {},
	afterApplyUpdatesCallback: (updatesStore: IDBObjectStore) => void = () => {},
): Promise<IDBObjectStore | null> {
	const db = idbPersistence.db;
	if (!db || idbPersistence._destroyed) return Promise.resolve(null);

	let updatesStore: IDBObjectStore;
	let entriesPromise: Promise<Array<{ key: number; value: unknown }>>;
	try {
		const tx = db.transaction([updatesStoreName], "readwrite");
		updatesStore = tx.objectStore(updatesStoreName);
		entriesPromise = readDocEntriesWithKeys(
			updatesStore,
			idbPersistence.name,
			idbPersistence._dbref,
		);
	} catch (err) {
		idbPersistence._handleDbError(err);
		return Promise.resolve(null);
	}

	return entriesPromise
		.then((entries) => {
			if (idbPersistence._destroyed || idbPersistence.db !== db) return null;
			const docUpdates: Uint8Array[] = [];
			for (const entry of entries) {
				if (isStoredUpdateRecord(entry.value)) {
					docUpdates.push(entry.value.update);
				} else if (entry.value instanceof Uint8Array) {
					docUpdates.push(entry.value);
				}
			}

			beforeApplyUpdatesCallback(updatesStore);
			Y.transact(
				idbPersistence.doc,
				() => {
					for (const val of docUpdates) Y.applyUpdate(idbPersistence.doc, val);
				},
				idbPersistence,
				false,
			);
			afterApplyUpdatesCallback(updatesStore);

			return readDocKeys(updatesStore, idbPersistence.name).then((docKeys) => {
				if (!idbPersistence._destroyed) {
					const lastKey = docKeys[docKeys.length - 1];
					idbPersistence._dbref =
						lastKey !== undefined ? lastKey + 1 : idbPersistence._dbref;
					idbPersistence._dbsize = docKeys.length;
				}
				return updatesStore;
			});
		})
		.catch((err) => {
			idbPersistence._handleDbError(err);
			return null;
		});
}

function withCompactionLock<T>(
	docName: string,
	fn: () => Promise<T>,
): Promise<T> {
	if (typeof navigator !== "undefined" && navigator.locks?.request) {
		return navigator.locks.request(`illumi-yjs-compact:${docName}`, fn);
	}
	return fn();
}

export function storeState(
	idbPersistence: IndexeddbPersistence,
	forceStore = true,
): Promise<void> {
	if (idbPersistence._destroyed || !idbPersistence.db) {
		return Promise.resolve();
	}
	return withCompactionLock(idbPersistence.name, () =>
		fetchUpdates(idbPersistence)
			.then((updatesStore) => {
				if (!updatesStore || idbPersistence._destroyed) return;
				if (!forceStore && idbPersistence._dbsize < PREFERRED_TRIM_SIZE) return;
				const encoded: StoredUpdateRecord = {
					doc: idbPersistence.name,
					update: Y.encodeStateAsUpdate(idbPersistence.doc),
				};
				return rtop<IDBValidKey>(updatesStore.add(encoded))
					.then((snapshotKey) => {
						const cutoff =
							typeof snapshotKey === "number"
								? snapshotKey
								: idbPersistence._dbref;
						return readDocKeys(updatesStore, idbPersistence.name).then((keys) =>
							Promise.all(
								keys
									.filter((key) => key < cutoff)
									.map((key) => rtop(updatesStore.delete(key))),
							),
						);
					})
					.then(() => readDocKeys(updatesStore, idbPersistence.name))
					.then((remainingKeys) => {
						if (!idbPersistence._destroyed) {
							const lastKey = remainingKeys[remainingKeys.length - 1];
							if (lastKey !== undefined) idbPersistence._dbref = lastKey + 1;
							idbPersistence._dbsize = remainingKeys.length;
						}
					});
			})
			.catch((err) => {
				idbPersistence._handleDbError(err);
			}),
	);
}
